/**
 * In-process bus for `evidence.*` SSE events plus the single toast helper every evidence
 * notification goes through.
 *
 * The app holds one EventSource (see `useSSE`). Rather than opening a second connection per
 * panel, the SSE hook publishes evidence events here and interested components subscribe.
 *
 * Toasts are deduplicated by `${evidence_id}:${notification_code}`: the backend both returns a
 * notification hint in the HTTP response and pushes the same notification over SSE to the
 * acting user, so without a shared id the user would see every upload/delete twice.
 */
import { showToast } from '../utils/toast';
import type { EvidenceSseEvent, NotificationHint, NotificationSeverity } from '../types/evidence';

type Listener = (event: EvidenceSseEvent) => void;

const listeners = new Set<Listener>();

export const subscribeToEvidenceEvents = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const publishEvidenceEvent = (event: EvidenceSseEvent): void => {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      console.error('Evidence event listener failed', error);
    }
  });
};

type ResyncListener = () => void;

const resyncListeners = new Set<ResyncListener>();

/**
 * A resync is published when the SSE connection comes back after a drop. Events emitted while it
 * was down are gone for good (the stream has no replay), so anything that mirrors server state
 * from events must reload once.
 */
export const subscribeToEvidenceResync = (listener: ResyncListener): (() => void) => {
  resyncListeners.add(listener);
  return () => {
    resyncListeners.delete(listener);
  };
};

export const publishEvidenceResync = (): void => {
  resyncListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('Evidence resync listener failed', error);
    }
  });
};

export interface EvidenceNotification extends NotificationHint {
  /** Used to dedupe the HTTP hint against the matching SSE event. */
  evidenceId?: string | null;
  /**
   * Explicit toast id for notifications with no evidence id that can still repeat (for
   * example a failing list refresh, fired once per SSE event). Overrides the evidence-id key.
   */
  dedupeKey?: string;
}

const toastFor: Record<NotificationSeverity, (message: string, options?: object) => unknown> = {
  success: showToast.success,
  error: showToast.error,
  warning: (message, options) => showToast.custom(message, { icon: '⚠️', ...options }),
  info: (message, options) => showToast.custom(message, { icon: 'ℹ️', ...options }),
};

export const notifyEvidence = (notification: EvidenceNotification): void => {
  const id =
    notification.dedupeKey ??
    (notification.evidenceId ? `${notification.evidenceId}:${notification.code}` : undefined);
  const severity: NotificationSeverity = toastFor[notification.severity] ? notification.severity : 'info';
  toastFor[severity](notification.message, id ? { id } : undefined);
};

export const notifyEvidenceEvent = (event: EvidenceSseEvent): void =>
  notifyEvidence({
    code: event.notification_code,
    message: event.message,
    severity: event.severity,
    evidenceId: event.evidence_id,
  });
